/**
 * Build-menu thumbnails: the actual entity art, cropped, so a hotbar slot shows
 * the thing you are about to build rather than four letters of shorthand.
 *
 * No scaling anywhere. A button-sized icon is a CROP of the sprite around its
 * most identifying part (the head of a tower, the top of a rig) — halving pixel
 * art would smear the 1 px outline the whole art pipeline exists to protect
 * (doc 10 §Legibility, doc 16 L2).
 */

import { buildEntity } from '../art/build.ts';
import { drawEconomyBuilding, drawWall, type PlaceholderRender } from '../art/placeholder.ts';
import { REGISTRY_DEFS, specById } from '../art/registry.ts';
import type { DepthPalette } from '../render/palette.ts';
import { PixelSurface } from '../render/surface.ts';



function render(def: string, depth: DepthPalette): PlaceholderRender {
  if (def === 'wall') return drawWall(depth, false, 0);
  if (REGISTRY_DEFS.has(def)) return buildEntity(specById(def), { depth, state: 'idle', phase: 0, facing: 1 });
  return drawEconomyBuilding(def, depth, 'working');
}

/** Crop `src` to the ink it actually contains, with a 1 px margin. */
function cropToInk(src: PixelSurface): PixelSurface {
  const b = src.inkBounds();
  if (!b) return src;
  const x0 = Math.max(0, b.x0 - 1);
  const y0 = Math.max(0, b.y0 - 1);
  const w = Math.min(src.width - x0, b.x1 - b.x0 + 3);
  const h = Math.min(src.height - y0, b.y1 - b.y0 + 3);
  const out = new PixelSurface(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) out.px(x, y, src.get(x0 + x, y0 + y), 'set');
  }
  return out.resetOps();
}

const full = new Map<string, PixelSurface>();
const icons = new Map<string, PixelSurface>();

/** The whole entity, trimmed — for tooltips, which have room for it. */
export function entityThumb(def: string, depth: DepthPalette): PixelSurface {
  let s = full.get(def);
  if (!s) {
    s = cropToInk(render(def, depth).body.surface);
    full.set(def, s);
  }
  return s;
}

/**
 * A `size`×`size` window on the entity, centred on its silhouette and anchored
 * near the top — where towers keep the part you recognise them by.
 */
export function entityIcon(def: string, depth: DepthPalette, size: number): PixelSurface {
  const key = `${def}:${size}`;
  let icon = icons.get(key);
  if (icon) return icon;

  const src = entityThumb(def, depth);
  icon = new PixelSurface(size, size);
  const ox = Math.round((src.width - size) / 2);
  // keep the top of the silhouette: the head reads, the plinth does not
  const oy = src.height > size ? Math.min(src.height - size, Math.max(0, Math.round(src.height * 0.12))) : Math.round((src.height - size) / 2);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) icon.px(x, y, src.get(ox + x, oy + y), 'set');
  }
  icons.set(key, icon.resetOps());
  return icon;
}
